import { Invoice, InvoiceStatus, PaymentMethod } from '@/shared/types';
import type { InvoiceItem, Payment, CreateInvoiceData, UpdateInvoiceData, RecordPaymentData, InvoiceFilter } from '@/shared/types/invoice';
import { supabaseService } from '@/shared/services/supabaseService';
import { authService } from '@/shared/services/realData/auth';
import { generateShareToken } from '@/shared/utils/shareToken';

/**
 * Invoice operations
 * Handles all invoice-related database operations using the actual invoices table
 */
export const invoiceService = {
  /**
   * Get all invoices for the current user with related client, job, items, and payments
   */
  async getInvoices(filter?: InvoiceFilter): Promise<Invoice[]> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    let query = supabaseService.client
      .from('invoices')
      .select(`
        *,
        client:clients(id, name, email, phone, address, company, logo_url),
        job:jobs(id, title, status),
        invoice_items(
          id,
          name,
          description,
          quantity,
          unit_price,
          total_price,
          order_index
        ),
        payments(
          id,
          amount,
          payment_date,
          payment_method,
          status,
          transaction_id,
          notes
        )
      `)
      .eq('user_id', supabaseUserId);

    // Apply filters
    if (filter?.status) {
      query = query.eq('status', filter.status);
    }
    if (filter?.clientId) {
      query = query.eq('client_id', filter.clientId);
    }
    if (filter?.search) {
      query = query.or(`title.ilike.%${filter.search}%,invoice_number.ilike.%${filter.search}%,description.ilike.%${filter.search}%`);
    }
    if (filter?.startDate) {
      query = query.gte('issue_date', filter.startDate);
    }
    if (filter?.endDate) {
      query = query.lte('issue_date', filter.endDate);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;

    if (!data || data.length === 0) return [];

    // Map database results to Invoice type
    return data.map((dbInvoice: any) => this.mapDatabaseInvoiceToInvoice(dbInvoice));
  },

  /**
   * Get a single invoice by ID
   */
  async getInvoiceById(id: string): Promise<Invoice | null> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    const { data, error } = await supabaseService.client
      .from('invoices')
      .select(`
        *,
        client:clients(id, name, email, phone, address, company, logo_url),
        job:jobs(id, title, status),
        invoice_items(
          id,
          name,
          description,
          quantity,
          unit_price,
          total_price,
          order_index
        ),
        payments(
          id,
          amount,
          payment_date,
          payment_method,
          status,
          transaction_id,
          notes
        )
      `)
      .eq('id', id)
      .eq('user_id', supabaseUserId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows returned
      throw error;
    }

    if (!data) return null;

    return this.mapDatabaseInvoiceToInvoice(data);
  },

  /**
   * Create a new invoice
   */
  async createInvoice(invoiceData: CreateInvoiceData): Promise<Invoice> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    // Generate invoice number
    const invoiceNumber = await this.generateInvoiceNumber();

    // Calculate totals
    const subtotal = invoiceData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const taxRate = invoiceData.taxRate || 0;
    const taxAmount = subtotal * (taxRate / 100);
    const totalAmount = subtotal + taxAmount;

    // Use RPC function to create invoice (bypasses RLS)
    const { data: newInvoice, error: invoiceError } = await supabaseService.client.rpc('create_invoice', {
      p_user_id: supabaseUserId,
      p_client_id: invoiceData.clientId,
      p_job_id: invoiceData.jobId || null,
      p_invoice_number: invoiceNumber,
      p_title: invoiceData.title,
      p_description: invoiceData.description || null,
      p_status: invoiceData.status || 'draft', // Use provided status or default to draft
      p_currency: invoiceData.currency,
      p_subtotal: subtotal,
      p_tax_rate: taxRate,
      p_tax_amount: taxAmount,
      p_total_amount: totalAmount,
      p_paid_amount: 0,
      p_issue_date: invoiceData.issueDate,
      p_due_date: invoiceData.dueDate,
      p_notes: invoiceData.notes || null,
      p_terms: invoiceData.terms || null
    });

    if (invoiceError) {
      console.error('RPC create_invoice error:', invoiceError);
      throw invoiceError;
    }

    if (!newInvoice) {
      throw new Error('Failed to create invoice: no data returned');
    }

    // Add public token and timestamps (these aren't set by the RPC function)
    const { data: updatedInvoice } = await supabaseService.client
      .from('invoices')
      .update({
        public_token: generateShareToken(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', newInvoice.id)
      .select()
      .single();

    const finalInvoice = updatedInvoice || newInvoice;

    // Insert invoice items
    if (invoiceData.items && invoiceData.items.length > 0) {
      const itemsToInsert = invoiceData.items.map((item, index) => ({
        invoice_id: finalInvoice.id,
        name: item.name,
        description: item.description || null,
        quantity: item.quantity,
        unit_price: item.unit_price,
        // total_price is a generated column - do NOT include in INSERT
        order_index: index
      }));

      const { error: itemsError } = await supabaseService.client
        .from('invoice_items')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;
    }

    // Return the complete invoice
    return (await this.getInvoiceById(finalInvoice.id))!;
  },

  /**
   * Update an existing invoice
   */
  async updateInvoice(id: string, invoiceData: UpdateInvoiceData): Promise<Invoice> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    // Check if invoice exists and belongs to user
    const existing = await this.getInvoiceById(id);
    if (!existing) throw new Error('Invoice not found');

    // Prevent updates to paid or cancelled invoices
    if (existing.status === 'paid' || existing.status === 'cancelled') {
      throw new Error('Cannot update a paid or cancelled invoice');
    }

    // Prepare update data
    const updateData: any = {
      updated_at: new Date().toISOString()
    };

    if (invoiceData.title !== undefined) updateData.title = invoiceData.title;
    if (invoiceData.description !== undefined) updateData.description = invoiceData.description;
    if (invoiceData.currency !== undefined) updateData.currency = invoiceData.currency;
    if (invoiceData.issueDate !== undefined) updateData.issue_date = invoiceData.issueDate;
    if (invoiceData.dueDate !== undefined) updateData.due_date = invoiceData.dueDate;
    if (invoiceData.notes !== undefined) updateData.notes = invoiceData.notes;
    if (invoiceData.terms !== undefined) updateData.terms = invoiceData.terms;
    if (invoiceData.status !== undefined) updateData.status = invoiceData.status;

    // Update items if provided
    if (invoiceData.items) {
      // Delete existing items
      const { error: deleteError } = await supabaseService.client
        .from('invoice_items')
        .delete()
        .eq('invoice_id', id);

      if (deleteError) throw deleteError;

      // Insert new items
      const itemsToInsert = invoiceData.items.map((item, index) => ({
        invoice_id: id,
        name: item.name,
        description: item.description || null,
        quantity: item.quantity,
        unit_price: item.unit_price,
        // total_price is a generated column - do NOT include in INSERT
        order_index: index
      }));

      const { error: itemsError } = await supabaseService.client
        .from('invoice_items')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;

      // Recalculate totals
      const subtotal = invoiceData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
      const taxRate = invoiceData.taxRate !== undefined ? invoiceData.taxRate : existing.tax_rate;
      const taxAmount = subtotal * (taxRate / 100);
      const totalAmount = subtotal + taxAmount;

      updateData.subtotal = subtotal;
      updateData.tax_rate = taxRate;
      updateData.tax_amount = taxAmount;
      updateData.total_amount = totalAmount;
    }

    // Update invoice
    const { error: updateError } = await supabaseService.client
      .from('invoices')
      .update(updateData)
      .eq('id', id)
      .eq('user_id', supabaseUserId);

    if (updateError) throw updateError;

    // Return the updated invoice
    return (await this.getInvoiceById(id))!;
  },

  /**
   * Delete an invoice (only if draft or cancelled)
   */
  async deleteInvoice(id: string): Promise<void> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    // Check if invoice exists and can be deleted
    const existing = await this.getInvoiceById(id);
    if (!existing) throw new Error('Invoice not found');

    if (existing.status !== 'draft' && existing.status !== 'cancelled') {
      throw new Error('Can only delete draft or cancelled invoices');
    }

    // Delete invoice items (will be cascaded anyway, but explicit is clearer)
    await supabaseService.client
      .from('invoice_items')
      .delete()
      .eq('invoice_id', id);

    // Delete invoice
    const { error } = await supabaseService.client
      .from('invoices')
      .delete()
      .eq('id', id)
      .eq('user_id', supabaseUserId);

    if (error) throw error;
  },

  /**
   * Get an invoice by its public token
   * Used for public invoice viewing without authentication
   */
  async getInvoiceByPublicToken(token: string): Promise<Invoice | null> {
    if (!token) throw new Error('Public token is required');

    const { data, error } = await supabaseService.client
      .from('invoices')
      .select(`
        *,
        client:clients(id, name, email, phone, address, company, logo_url),
        job:jobs(id, title, status),
        invoice_items(
          id,
          name,
          description,
          quantity,
          unit_price,
          total_price,
          order_index
        ),
        payments(
          id,
          amount,
          payment_date,
          payment_method,
          status,
          transaction_id,
          notes
        )
      `)
      .eq('public_token', token)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    if (!data) return null;

    return this.mapDatabaseInvoiceToInvoice(data);
  },

  /**
   * Record a payment for an invoice
   */
  async recordPayment(invoiceId: string, paymentData: RecordPaymentData): Promise<Payment> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    // Get the invoice
    const invoice = await this.getInvoiceById(invoiceId);
    if (!invoice) throw new Error('Invoice not found');

    // Calculate new paid amount
    const newPaidAmount = invoice.paid_amount + paymentData.amount;

    // Check if payment exceeds total
    if (newPaidAmount > invoice.total_amount) {
      throw new Error(`Payment amount (${paymentData.amount}) would exceed the invoice total (${invoice.total_amount}). Current paid: ${invoice.paid_amount}`);
    }

    // Insert payment record
    const { data: newPayment, error: paymentError } = await supabaseService.client
      .from('payments')
      .insert({
        user_id: supabaseUserId,
        client_id: invoice.client_id,
        invoice_id: invoiceId,
        amount: paymentData.amount,
        currency: invoice.currency,
        payment_method: paymentData.paymentMethod,
        payment_date: paymentData.paymentDate,
        transaction_id: paymentData.transactionId || null,
        notes: paymentData.notes || null,
        status: 'completed',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (paymentError) throw paymentError;

    // Update invoice paid_amount
    const updateData: any = {
      paid_amount: newPaidAmount,
      updated_at: new Date().toISOString()
    };

    // Update status based on payment
    if (newPaidAmount >= invoice.total_amount) {
      updateData.status = 'paid';
    } else if (newPaidAmount > 0 && invoice.status === 'draft') {
      updateData.status = 'sent';
    }

    const { error: updateError } = await supabaseService.client
      .from('invoices')
      .update(updateData)
      .eq('id', invoiceId)
      .eq('user_id', supabaseUserId);

    if (updateError) throw updateError;

    return this.mapDatabasePaymentToPayment(newPayment);
  },

  /**
   * Get all payments for an invoice
   */
  async getInvoicePayments(invoiceId: string): Promise<Payment[]> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    const { data, error } = await supabaseService.client
      .from('payments')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('payment_date', { ascending: false });

    if (error) throw error;

    if (!data || data.length === 0) return [];

    return data.map((payment: any) => this.mapDatabasePaymentToPayment(payment));
  },

  /**
   * Update invoice status
   */
  async updateInvoiceStatus(invoiceId: string, status: InvoiceStatus): Promise<Invoice> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    const { error } = await supabaseService.client
      .from('invoices')
      .update({
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', invoiceId)
      .eq('user_id', supabaseUserId);

    if (error) throw error;

    return (await this.getInvoiceById(invoiceId))!;
  },

  /**
   * Check if an invoice is overdue
   */
  async checkOverdueInvoices(): Promise<void> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    const today = new Date().toISOString().split('T')[0];

    // Find all sent invoices that are past due date
    const { data: overdueInvoices, error } = await supabaseService.client
      .from('invoices')
      .select('id')
      .eq('user_id', supabaseUserId)
      .eq('status', 'sent')
      .lt('due_date', today);

    if (error) throw error;

    // Update their status to overdue
    if (overdueInvoices && overdueInvoices.length > 0) {
      const invoiceIds = overdueInvoices.map((inv: any) => inv.id);

      await supabaseService.client
        .from('invoices')
        .update({ status: 'overdue' })
        .in('id', invoiceIds);
    }
  },

  /**
   * Regenerate public token for an invoice
   */
  async regeneratePublicToken(invoiceId: string): Promise<string> {
    const newToken = generateShareToken();

    const { error } = await supabaseService.client
      .from('invoices')
      .update({
        public_token: newToken,
        updated_at: new Date().toISOString()
      })
      .eq('id', invoiceId);

    if (error) throw error;
    return newToken;
  },

  /**
   * Generate a unique invoice number
   */
  async generateInvoiceNumber(): Promise<string> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    // Get the maximum invoice number for this user
    const { data, error } = await supabaseService.client
      .from('invoices')
      .select('invoice_number')
      .eq('user_id', supabaseUserId)
      .order('invoice_number', { ascending: false })
      .limit(1);

    if (error) throw error;

    let nextNum = 1;
    if (data && data.length > 0) {
      // Extract numeric part from invoice number (e.g., "INV-0001" -> 1)
      const lastInvoice = data[0];
      if (!lastInvoice) {
        return `INV-0001`;
      }
      const lastNumber = lastInvoice.invoice_number;
      const match = lastNumber.match(/(\d+)$/);
      if (match) {
        nextNum = parseInt(match[1]) + 1;
      }
    }

    return `INV-${String(nextNum).padStart(4, '0')}`;
  },

  /**
   * Map database invoice to application Invoice type
   */
  mapDatabaseInvoiceToInvoice(dbInvoice: any): Invoice {
    return {
      id: dbInvoice.id,
      user_id: dbInvoice.user_id,
      userId: dbInvoice.user_id,
      client_id: dbInvoice.client_id,
      clientId: dbInvoice.client_id,
      job_id: dbInvoice.job_id,
      jobId: dbInvoice.job_id,
      invoice_number: dbInvoice.invoice_number,
      invoiceNumber: dbInvoice.invoice_number,
      title: dbInvoice.title,
      description: dbInvoice.description,
      status: dbInvoice.status,
      currency: dbInvoice.currency,
      subtotal: Number(dbInvoice.subtotal) || 0,
      tax_rate: Number(dbInvoice.tax_rate) || 0,
      taxRate: Number(dbInvoice.tax_rate) || 0,
      tax_amount: Number(dbInvoice.tax_amount) || 0,
      taxAmount: Number(dbInvoice.tax_amount) || 0,
      total_amount: Number(dbInvoice.total_amount) || 0,
      totalAmount: Number(dbInvoice.total_amount) || 0,
      paid_amount: Number(dbInvoice.paid_amount) || 0,
      amountPaid: Number(dbInvoice.paid_amount) || 0,
      issue_date: dbInvoice.issue_date,
      dateIssued: dbInvoice.issue_date,
      due_date: dbInvoice.due_date,
      dueDate: dbInvoice.due_date,
      notes: dbInvoice.notes,
      terms: dbInvoice.terms,
      public_token: dbInvoice.public_token,
      created_at: dbInvoice.created_at,
      updated_at: dbInvoice.updated_at,
      items: (dbInvoice.invoice_items || []).map((item: any) => ({
        id: item.id,
        invoice_id: item.invoice_id,
        invoiceId: item.invoice_id,
        name: item.name,
        description: item.description,
        quantity: Number(item.quantity),
        unit_price: Number(item.unit_price),
        rate: Number(item.unit_price),
        total_price: Number(item.total_price),
        amount: Number(item.total_price),
        order_index: item.order_index,
        created_at: item.created_at
      })),
      payments: (dbInvoice.payments || []).map((payment: any) =>
        this.mapDatabasePaymentToPayment(payment)
      )
    };
  },

  /**
   * Map database payment to application Payment type
   */
  mapDatabasePaymentToPayment(dbPayment: any): Payment {
    return {
      id: dbPayment.id,
      user_id: dbPayment.user_id,
      client_id: dbPayment.client_id,
      clientId: dbPayment.client_id,
      invoice_id: dbPayment.invoice_id,
      invoiceId: dbPayment.invoice_id,
      amount: Number(dbPayment.amount),
      currency: dbPayment.currency,
      payment_method: dbPayment.payment_method,
      paymentMethod: dbPayment.payment_method,
      payment_method_id: dbPayment.payment_method_id,
      payment_date: dbPayment.payment_date,
      reference: dbPayment.reference,
      transaction_id: dbPayment.transaction_id,
      paypalPaymentId: dbPayment.transaction_id,
      notes: dbPayment.notes,
      status: dbPayment.status,
      created_at: dbPayment.created_at,
      updated_at: dbPayment.updated_at
    };
  }
};
